import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Crown, 
  Users, 
  Clock, 
  Phone, 
  Contact, 
  Megaphone,
  AlertTriangle,
  ArrowUpRight,
  Zap,
  Sparkles,
  TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_API_URL}/api`;

// Plan badge colors
const PLAN_COLORS = {
  free_trial: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  starter: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  professional: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  enterprise: "bg-amber-500/20 text-amber-400 border-amber-500/30"
};

const PLAN_GRADIENTS = {
  free_trial: "from-emerald-500 to-teal-500",
  starter: "from-blue-500 to-cyan-500",
  professional: "from-purple-500 to-pink-500",
  enterprise: "from-amber-500 to-orange-500"
};

// Resource icons
const RESOURCE_ICONS = {
  agents: Users,
  minutes: Clock,
  contacts: Contact,
  campaigns: Megaphone,
  phone_numbers: Phone
};

// Resource labels
const RESOURCE_LABELS = {
  agents: "AI Agents",
  minutes: "Call Minutes",
  contacts: "Contacts",
  campaigns: "Campaigns",
  phone_numbers: "Phone Numbers"
};

/**
 * Compact version for sidebar
 */
export function PlanBadgeCompact({ className }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const plan = user?.subscription_plan || "free_trial";
  const planName = plan.replace("_", " ").toUpperCase();

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Badge className={cn("text-xs font-medium", PLAN_COLORS[plan])}>
        <Crown className="w-3 h-3 mr-1" />
        {planName}
      </Badge>
      {plan === "free_trial" && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-primary hover:text-primary"
          onClick={() => navigate("/dashboard/billing")}
        >
          Upgrade
          <ArrowUpRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
}

/**
 * Sidebar widget with usage bars
 */
export function PlanUsageSidebar({ className }) {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/usage/stats`, { headers: getAuthHeader() });
      setUsage(res.data);
    } catch (err) {
      console.error("Failed to fetch usage:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usage) return null;

  const hasWarnings = usage.warnings?.length > 0;
  const plan = usage.plan;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Plan Badge */}
      <div className="flex items-center justify-between">
        <Badge className={cn("text-xs font-semibold", PLAN_COLORS[plan.key])}>
          <Crown className="w-3 h-3 mr-1" />
          {plan.name}
        </Badge>
        {plan.key !== "enterprise" && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => navigate("/dashboard/billing")}
          >
            Upgrade
          </Button>
        )}
      </div>

      {/* Key Usage Bars */}
      <div className="space-y-2">
        {["agents", "minutes"].map((resource) => {
          const data = usage.usage[resource];
          if (!data) return null;
          const Icon = RESOURCE_ICONS[resource];
          const isWarning = data.percentage >= 80;
          const isCritical = data.percentage >= 100;

          return (
            <div key={resource} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Icon className="w-3 h-3" />
                  {RESOURCE_LABELS[resource]}
                </span>
                <span className={cn(
                  "font-medium",
                  isCritical ? "text-red-400" : isWarning ? "text-amber-400" : "text-foreground"
                )}>
                  {data.unlimited ? "∞" : `${data.used}/${data.limit}`}
                </span>
              </div>
              {!data.unlimited && (
                <Progress 
                  value={data.percentage} 
                  className={cn(
                    "h-1.5",
                    isCritical ? "[&>div]:bg-red-500" : isWarning ? "[&>div]:bg-amber-500" : ""
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Warning Banner */}
      {hasWarnings && usage.warnings.some(w => w.level === "critical") && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-300">Limit reached</p>
        </div>
      )}
    </div>
  );
}

/**
 * Full dashboard widget
 */
export function PlanUsageWidget({ className }) {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/usage/stats`, { headers: getAuthHeader() });
      setUsage(res.data);
    } catch (err) {
      console.error("Failed to fetch usage:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={cn("bg-card/50 border-border", className)}>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-32 bg-muted rounded" />
            <div className="space-y-2">
              <div className="h-4 w-full bg-muted rounded" />
              <div className="h-4 w-3/4 bg-muted rounded" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!usage) return null;

  const plan = usage.plan;
  const hasWarnings = usage.warnings?.length > 0;

  return (
    <Card className={cn("bg-card/50 border-border overflow-hidden", className)}>
      {/* Header with gradient */}
      <div className={cn("p-4 bg-gradient-to-r", PLAN_GRADIENTS[plan.key])}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">{plan.name}</h3>
              <p className="text-xs text-white/80">
                {plan.price === 0 ? "Free for 14 days" : `$${plan.price}/month`}
              </p>
            </div>
          </div>
          {plan.key !== "enterprise" && (
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/dashboard/billing")}
            >
              <Zap className="w-4 h-4 mr-1" />
              Upgrade
            </Button>
          )}
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Warning Banners */}
        {hasWarnings && (
          <div className="space-y-2">
            {usage.warnings.map((warning, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex items-center gap-2 p-3 rounded-lg text-sm",
                  warning.level === "critical"
                    ? "bg-red-500/10 border border-red-500/30 text-red-300"
                    : "bg-amber-500/10 border border-amber-500/30 text-amber-300"
                )}
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>{warning.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Usage Grid */}
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(usage.usage).map(([resource, data]) => {
            const Icon = RESOURCE_ICONS[resource];
            const isWarning = data.percentage >= 80 && !data.unlimited;
            const isCritical = data.percentage >= 100 && !data.unlimited;

            return (
              <div key={resource} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon className="w-4 h-4" />
                    {RESOURCE_LABELS[resource]}
                  </span>
                </div>
                <div className="flex items-end justify-between">
                  <span className={cn(
                    "text-2xl font-bold",
                    isCritical ? "text-red-400" : isWarning ? "text-amber-400" : "text-foreground"
                  )}>
                    {data.used}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    / {data.unlimited ? "∞" : data.limit}
                  </span>
                </div>
                {!data.unlimited && (
                  <Progress
                    value={data.percentage}
                    className={cn(
                      "h-2",
                      isCritical ? "[&>div]:bg-red-500" : isWarning ? "[&>div]:bg-amber-500" : ""
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Need more capacity?</span>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-primary"
              onClick={() => navigate("/dashboard/billing")}
            >
              View Plans
              <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Hook to get usage data and check capabilities
 */
export function useUsageStats() {
  const { getAuthHeader } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/usage/stats`, { headers: getAuthHeader() });
      setUsage(res.data);
    } catch (err) {
      console.error("Failed to fetch usage:", err);
    } finally {
      setLoading(false);
    }
  };

  return {
    usage,
    loading,
    refresh: fetchUsage,
    canCreateAgent: usage?.can_create_agent ?? true,
    canCreateCampaign: usage?.can_create_campaign ?? true,
    canAddContact: usage?.can_add_contact ?? true,
    canAddPhone: usage?.can_add_phone ?? true,
    plan: usage?.plan ?? { key: "free_trial", name: "Free Trial" }
  };
}

export default PlanUsageWidget;
